import type {
  HoldingsSignalItem,
  HoldingsSignalsPagination,
  NotifyClient,
} from "@/src/features/api/NotifyApi";

import { QUERY_KEYS } from "@config";
import { rootStore } from "@store";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useOspApiClientProvider } from "@hooks";
import { useSnapshot } from "valtio";
import { wrapperResult } from "@api/osp";

export type NotifyListHoldingsSignalsQueryParams = Omit<
  Parameters<NotifyClient<any>["signals"]["listHoldingsSignals"]>[0],
  "next_token"
>;

export interface UseNotifyHoldingsSignalsInfiniteOptions {
  /**
   * Query fields and page size; `next_token` is managed by the hook.
   * Memoize when stable to avoid redundant refetches.
   */
  queryParams: NotifyListHoldingsSignalsQueryParams;
  /** Combined with login state: requests only run when logged in. */
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

export interface UseNotifyHoldingsSignalsInfiniteResult {
  /** All loaded pages merged: `HoldingsSignalItem[]` (not pagination wrapper). */
  rows: HoldingsSignalItem[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: ReturnType<typeof useInfiniteQuery>["fetchNextPage"];
  refetch: ReturnType<typeof useInfiniteQuery>["refetch"];
  error: unknown;
}

/**
 * Infinite list for GET /signals/holdings (notify) using cursor `next_token`.
 * Pass all required query fields via `queryParams` (except `next_token`).
 *
 * Same as `useNotifyMessageInfinite`: exposes merged `rows` only; cursor uses
 * `next_token` between pages.
 */
export const useNotifyHoldingsSignalsInfinite = ({
  queryParams,
  enabled = true,
  staleTime = 30 * 1000,
  gcTime = 30 * 1000,
}: UseNotifyHoldingsSignalsInfiniteOptions): UseNotifyHoldingsSignalsInfiniteResult => {
  const { notifyApi } = useOspApiClientProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id;
  const requestQueryParams = useMemo(
    () => ({
      ...queryParams,
      ...(ownerId ? { owner_id: ownerId } : {}),
    }),
    [ownerId, queryParams],
  );
  const result = useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.NOTIFY_HOLDINGS_SIGNALS_INFINITE,
      requestQueryParams,
      ownerId,
      isLoggedIn,
    ],
    queryFn: async ({
      pageParam,
    }: {
      pageParam: string | undefined;
    }): Promise<HoldingsSignalsPagination> => {
      if (!notifyApi?.signals?.listHoldingsSignals) {
        throw new Error("Notify holdings signals API is not available");
      }
      const data = await wrapperResult(
        notifyApi.signals.listHoldingsSignals({
          ...requestQueryParams,
          ...(pageParam !== undefined && pageParam !== ""
            ? { next_token: pageParam }
            : {}),
        }),
      );
      return data as HoldingsSignalsPagination;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      const token = lastPage?.next_token;
      if (typeof token === "string" && token.length > 0) {
        return token;
      }
      return undefined;
    },
    enabled: !!notifyApi && !!ownerId && isLoggedIn && enabled,
    staleTime,
    gcTime,
  });

  const rows = useMemo(
    () => result.data?.pages.flatMap((page) => page.rows ?? []) ?? [],
    [result.data],
  );

  const total = result.data?.pages[0]?.total ?? 0;

  return {
    rows,
    total,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isFetchingNextPage: result.isFetchingNextPage,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    refetch: result.refetch,
    error: result.error,
  };
};
